import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { keyframes } from "@mui/system";
import cardMandala from "../../assets/Images/TempleDetail/card-mandala.svg";
import { T, FONT, SectionCard, SectionHeading, Photo, FitPhoto } from "./shared";

// Figma 3463:7346 "History — Eras (interactive)". The photo on the left is pinned; the
// reading column scrolls through the eras and the photo, caption, rail and thumbnails follow.
// Clicking a dot, an era or a thumbnail scrolls that era into view.
// Phones: MobileHistory below — the section pins while page scrolling steps through the eras.

// Phones: page scroll per era while the section is pinned.
const STEP_VH = 55;

const eraIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// One era's photo, never cropped (the frame is portrait, most temple photos are landscape).
const EraImage = ({ src, visible }) => (
  <Box sx={{ position: "absolute", inset: 0, opacity: visible ? 1 : 0, transition: "opacity 0.5s ease" }}>
    <FitPhoto src={src} />
  </Box>
);

// The photo with its caption, counter and thumbnails (shared by both layouts).
const EraPhoto = ({ eras, active, onPick, height, width = "100%" }) => {
  const current = eras[active] || eras[0];
  return (
    <Box sx={{ position: "relative", width, height, borderRadius: "24px", overflow: "hidden", background: T.maroon, flexShrink: 0 }}>
      {eras.map((era, i) => (
        <EraImage key={i} src={era.image} visible={i === active} />
      ))}
      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(26,5,8,0.35) 0%, rgba(26,5,8,0) 30%, rgba(36,5,8,0.25) 62%, rgba(36,5,8,0.95) 100%)" }} />
      <Box sx={{ position: "absolute", left: "20px", top: "20px", px: "12px", py: "6px", borderRadius: "100px", background: "rgba(26,8,8,0.55)", backdropFilter: "blur(6px)" }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "12px", lineHeight: "16px", color: "#FFFFFF" }}>
          {String(active + 1).padStart(2, "0")} / {String(eras.length).padStart(2, "0")}
        </Typography>
      </Box>
      <Typography sx={{ position: "absolute", left: "24px", right: "24px", bottom: "78px", fontFamily: FONT, fontWeight: 700, fontSize: "13px", lineHeight: "18px", color: T.goldLight }}>
        {current.caption}
      </Typography>
      <Box sx={{ position: "absolute", left: "24px", bottom: "24px", display: "flex", gap: "8px", alignItems: "flex-end" }}>
        {eras.map((era, i) => (
          <Box
            key={i}
            component="button"
            type="button"
            aria-label={`Show ${era.caption || era.year || `era ${i + 1}`}`}
            onClick={() => onPick(i)}
            sx={{
              width: i === active ? "44px" : "36px",
              height: "36px",
              p: 0,
              borderRadius: "8px",
              border: i === active ? `2.5px solid ${T.red}` : "1px solid rgba(255,255,255,0.5)",
              opacity: i === active ? 1 : 0.7,
              overflow: "hidden",
              cursor: "pointer",
              background: T.maroon,
              boxSizing: "border-box",
              transition: "width 0.2s, opacity 0.2s",
              "&:hover": { opacity: 1 },
              "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "2px" },
            }}
          >
            <Photo src={era.image} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Phones: the section pins below the top of the screen; each STEP_VH of page scroll moves to the
// next era (photo, caption, progress and text follow). After the last era the pin releases and
// the page carries on — no scroll area inside the page.
const MobileHistory = ({ history, eras }) => {
  const trackRef = useRef(null);
  const panelRef = useRef(null);
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [panelHeight, setPanelHeight] = useState(0);
  const pinned = eras.length > 1;

  // The track is the panel's own height plus the scroll spent on the eras.
  useEffect(() => {
    const panel = panelRef.current;
    if (!pinned || !panel) return undefined;
    const ro = new ResizeObserver(() => setPanelHeight(panel.offsetHeight));
    ro.observe(panel);
    return () => ro.disconnect();
  }, [pinned]);

  useEffect(() => {
    if (!pinned) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const track = trackRef.current;
      if (!track) return;
      const range = track.offsetHeight - panelRef.current.offsetHeight; // scroll while pinned
      const scrolled = Math.min(Math.max(-track.getBoundingClientRect().top + PIN_TOP, 0), range);
      const p = range > 0 ? scrolled / range : 0;
      setProgress(p);
      setActive(Math.min(eras.length - 1, Math.floor(p * eras.length)));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [pinned, eras.length, panelHeight]);

  // Thumbnail tap: scroll the page to the middle of that era's stretch.
  const goTo = (i) => {
    const track = trackRef.current;
    if (!pinned || !track) return setActive(i);
    const range = track.offsetHeight - panelRef.current.offsetHeight;
    const top = track.getBoundingClientRect().top + window.scrollY - PIN_TOP + ((i + 0.5) / eras.length) * range;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const era = eras[active] || eras[0];
  const panel = (
    <Box
      ref={panelRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        background: "#FFFFFF",
        // pinned: exactly the visible area between the top and the sticky "Offer Your Seva" bar
        ...(pinned && { height: `calc(100svh - ${PIN_TOP + BOTTOM_BAR}px)`, minHeight: "480px" }),
      }}
    >
      <Box sx={{ flex: pinned ? "1 1 auto" : "none", minHeight: pinned ? "220px" : 0, display: "flex" }}>
        <EraPhoto eras={eras} active={active} onPick={goTo} height={pinned ? "100%" : "300px"} />
      </Box>

      {pinned && (
        <Box aria-hidden="true" sx={{ display: "flex", gap: "6px" }}>
          {eras.map((_, i) => {
            const fill = Math.min(Math.max(progress * eras.length - i, 0), 1);
            return (
              <Box key={i} sx={{ flex: 1, height: "4px", borderRadius: "2px", background: T.sand, overflow: "hidden" }}>
                <Box sx={{ width: `${fill * 100}%`, height: "100%", background: T.red }} />
              </Box>
            );
          })}
        </Box>
      )}

      {/* Only the current era's text: the photo above takes whatever height is left, so a short
          era leaves no blank space under it */}
      <Box
        key={active}
        aria-live="polite"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          flexShrink: 0,
          pb: pinned ? "8px" : 0,
          "@media (prefers-reduced-motion: no-preference)": { animation: `${eraIn} 0.35s ease-out both` },
        }}
      >
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: "14px", letterSpacing: "1.1px", color: era.accent ? T.red : T.gold, whiteSpace: "pre-wrap" }}>{era.tag}</Typography>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "34px", lineHeight: "38px", color: era.accent ? T.red : T.gold }}>{era.year}</Typography>
        <Typography component="h3" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "19px", lineHeight: "26px", color: T.ink }}>{era.title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: "15px", lineHeight: "23px", color: T.body }}>{era.body}</Typography>
      </Box>
    </Box>
  );

  return (
    // overflow stays visible so the panel can stick; the mandala is clipped on its own
    <SectionCard id="history" sx={{ display: "flex", flexDirection: "column", gap: "20px", overflow: "visible" }}>
      <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, borderRadius: "inherit", overflow: "hidden", pointerEvents: "none" }}>
        <Box component="img" src={cardMandala} alt="" sx={{ position: "absolute", right: "-110px", top: "-110px", width: "300px", height: "300px", opacity: 0.09 }} />
      </Box>
      <SectionHeading eyebrow="Read the history" hindi="॥ इतिहास ॥" title={history.title} size={24} />
      {pinned ? (
        <Box ref={trackRef} sx={{ position: "relative", height: panelHeight ? `calc(${panelHeight}px + ${eras.length * STEP_VH}svh)` : "auto" }}>
          <Box sx={{ position: "sticky", top: `${PIN_TOP}px` }}>{panel}</Box>
        </Box>
      ) : (
        panel
      )}
    </SectionCard>
  );
};

// Distance from the top of the screen while pinned, and the room kept for the page's sticky
// "Offer Your Seva" bar at the bottom on phones.
const PIN_TOP = 16;
const BOTTOM_BAR = 96;

const HistoryEras = ({ history }) => {
  const isMobile = useMediaQuery("(max-width:760px)");
  const eras = history?.eras || [];
  const [active, setActive] = useState(0);
  const columnRef = useRef(null);
  const eraRefs = useRef([]);
  const [railHeight, setRailHeight] = useState(0);
  const [dotTops, setDotTops] = useState([]);
  const [thumb, setThumb] = useState({ top: 0, height: 0 });

  // Positions of each era inside the scrolling column (for the rail dots).
  const measure = useCallback(() => {
    const col = columnRef.current;
    if (!col) return;
    setDotTops(eraRefs.current.map((el) => (el ? el.offsetTop : 0)));
    setRailHeight(col.scrollHeight);
    const visible = col.clientHeight / col.scrollHeight;
    setThumb({ height: Math.max(40, col.clientHeight * visible), top: 0 });
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, eras.length]);

  const onScroll = () => {
    const col = columnRef.current;
    if (!col) return;
    // Active era = the last one whose top has passed the upper third of the column
    const line = col.scrollTop + col.clientHeight * 0.33;
    let idx = 0;
    eraRefs.current.forEach((el, i) => {
      if (el && el.offsetTop <= line) idx = i;
    });
    // At the very bottom the last era is active even if it never reaches the line
    if (col.scrollTop + col.clientHeight >= col.scrollHeight - 4) idx = eras.length - 1;
    setActive(idx);
    const max = col.scrollHeight - col.clientHeight;
    setThumb((t) => ({ ...t, top: max > 0 ? (col.scrollTop / max) * (col.clientHeight - t.height) : 0 }));
  };

  const goTo = (i) => {
    const col = columnRef.current;
    const el = eraRefs.current[i];
    setActive(i);
    if (col && el) col.scrollTo({ top: el.offsetTop, behavior: "smooth" });
  };

  if (!eras.length) return null;
  if (isMobile) return <MobileHistory history={history} eras={eras} />;
  const current = eras[active] || eras[0];
  const progressTop = 6;
  const progressHeight = Math.max(0, (dotTops[active] ?? 0) - progressTop + 6);

  const photo = (
    <Box
      sx={{
        position: "relative",
        width: isMobile ? "100%" : "320px",
        height: isMobile ? "360px" : "540px",
        borderRadius: "24px",
        overflow: "hidden",
        background: T.maroon,
        flexShrink: 0,
      }}
    >
      {eras.map((era, i) => (
        <EraImage key={i} src={era.image} visible={i === active} />
      ))}
      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(26,5,8,0.35) 0%, rgba(26,5,8,0) 30%, rgba(36,5,8,0.25) 62%, rgba(36,5,8,0.95) 100%)" }} />
      <Box sx={{ position: "absolute", left: "20px", top: "20px", px: "12px", py: "6px", borderRadius: "100px", background: "rgba(26,8,8,0.55)", backdropFilter: "blur(6px)" }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "12px", lineHeight: "16px", color: "#FFFFFF" }}>
          {String(active + 1).padStart(2, "0")} / {String(eras.length).padStart(2, "0")}
        </Typography>
      </Box>
      <Typography sx={{ position: "absolute", left: "24px", bottom: "78px", fontFamily: FONT, fontWeight: 700, fontSize: "13px", lineHeight: "18px", color: T.goldLight }}>
        {current.caption}
      </Typography>
      <Box sx={{ position: "absolute", left: "24px", bottom: "24px", display: "flex", gap: "8px", alignItems: "flex-end" }}>
        {eras.map((era, i) => (
          <Box
            key={i}
            component="button"
            type="button"
            aria-label={`Show ${era.caption}`}
            onClick={() => goTo(i)}
            sx={{
              width: i === active ? "44px" : "36px",
              height: "36px",
              p: 0,
              borderRadius: "8px",
              border: i === active ? `2.5px solid ${T.red}` : "1px solid rgba(255,255,255,0.5)",
              opacity: i === active ? 1 : 0.7,
              overflow: "hidden",
              cursor: "pointer",
              background: T.maroon,
              boxSizing: "border-box",
              transition: "width 0.2s, opacity 0.2s",
              "&:hover": { opacity: 1 },
              "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "2px" },
            }}
          >
            <Photo src={era.image} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <SectionCard id="history" sx={{ display: "flex", flexDirection: "column", gap: "28px" }}>
      <Box component="img" src={cardMandala} alt="" sx={{ position: "absolute", right: "-110px", top: "-110px", width: "300px", height: "300px", opacity: 0.09, pointerEvents: "none" }} />
      <SectionHeading eyebrow="Read the history" hindi="॥ इतिहास ॥" title={history.title} size={24} />

      <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "28px", alignItems: "flex-start" }}>
        {photo}

        {/* Reading column */}
        {/* Phones show every era in the page flow; tablets/desktop scroll them inside the column */}
        <Box sx={{ position: "relative", flex: isMobile ? "none" : 1, minWidth: 0, width: "100%", height: isMobile ? "auto" : "540px" }}>
          <Box
            ref={columnRef}
            onScroll={onScroll}
            tabIndex={0}
            aria-label="History of the temple"
            sx={{
              position: "relative",
              height: isMobile ? "auto" : "100%",
              overflowY: isMobile ? "visible" : "auto",
              overflowX: "hidden",
              pr: "16px",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              "&:focus-visible": { outline: `2px solid ${T.gold}`, outlineOffset: "4px", borderRadius: "8px" },
            }}
          >
            {/* Progress rail */}
            <Box sx={{ position: "absolute", left: 0, top: 0, width: "22px", height: `${railHeight}px` }}>
              <Box sx={{ position: "absolute", left: "10px", top: "6px", bottom: 0, width: "2px", background: T.sand }} />
              <Box sx={{ position: "absolute", left: "10px", top: `${progressTop}px`, width: "2px", height: `${progressHeight}px`, background: T.red, transition: "height 0.35s ease" }} />
              {eras.map((era, i) => {
                const isActive = i === active;
                const top = (dotTops[i] ?? 0) + 6;
                return (
                  <Box
                    key={i}
                    component="button"
                    type="button"
                    aria-label={`Go to ${era.caption}`}
                    onClick={() => goTo(i)}
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: `${top - 11}px`,
                      width: "22px",
                      height: "22px",
                      p: 0,
                      border: "none",
                      borderRadius: "11px",
                      background: isActive ? "rgba(236,27,38,0.15)" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "background 0.25s",
                      "&:focus-visible": { outline: `2px solid ${T.gold}`, outlineOffset: "1px" },
                    }}
                  >
                    <Box
                      sx={{
                        width: isActive ? "12px" : "10px",
                        height: isActive ? "12px" : "10px",
                        boxSizing: "border-box",
                        borderRadius: "50%",
                        background: isActive ? T.red : "#FFFFFF",
                        border: isActive ? "none" : `2px solid ${era.accent ? T.red : T.gold}`,
                        transition: "all 0.25s",
                      }}
                    />
                  </Box>
                );
              })}
            </Box>

            {/* Eras */}
            <Box sx={{ pl: "40px", display: "flex", flexDirection: "column", gap: isMobile ? "32px" : "44px", pb: isMobile ? 0 : "24px" }}>
              {eras.map((era, i) => {
                const isActive = i === active;
                return (
                  <Box
                    key={i}
                    ref={(el) => (eraRefs.current[i] = el)}
                    onClick={() => !isActive && goTo(i)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      opacity: isActive ? 1 : 0.45,
                      cursor: isActive ? "default" : "pointer",
                      transition: "opacity 0.35s ease",
                    }}
                  >
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: "14px", letterSpacing: "1.1px", color: isActive ? T.red : T.gold, whiteSpace: "pre" }}>{era.tag}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "36px" : "44px", lineHeight: isMobile ? "40px" : "48px", color: era.accent ? T.red : T.gold }}>{era.year}</Typography>
                    <Typography component="h3" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "19px" : "22px", lineHeight: "28px", color: T.ink }}>{era.title}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: "15px", lineHeight: "24px", color: T.body }}>{era.body}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Custom scrollbar */}
          {!isMobile && (
            <Box sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "3px", borderRadius: "2px", background: "#EDE0D1" }}>
              <Box sx={{ position: "absolute", left: 0, width: "3px", borderRadius: "2px", background: "#CC2926", top: `${thumb.top}px`, height: `${thumb.height}px` }} />
            </Box>
          )}
        </Box>
      </Box>
    </SectionCard>
  );
};

export default HistoryEras;
